import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip as MuiTooltip,
  IconButton,
  Tabs,
  Tab,
} from '@material-ui/core';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { useHistory } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, LineChart, Line, CartesianGrid } from 'recharts';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';

const GET_ADMIN_SPRINTS = gql`
  query AdminSprints($managerId: ID, $userId: ID, $status: String, $from: ISO8601Date, $to: ISO8601Date) {
    adminSprints(managerId: $managerId, userId: $userId, status: $status, from: $from, to: $to) {
      id
      name
      start_on
      end_on
      status
      progress_percent_cached
      items_total
      items_completed
      user { id allowed_name }
    }
    adminSprintAggregates(from: $from, to: $to) {
      adoption_over_time { date value }
      completion_rate_over_time { date value }
      blocked_percent_over_time { date value }
      completion_by_category { category completed total percent }
    }
  }
`;

const GET_ADMIN_SPRINTS_PAGED = gql`
  query AdminSprintsPaged($managerId: ID, $userId: ID, $status: String, $from: ISO8601Date, $to: ISO8601Date, $limit: Int, $offset: Int, $orderBy: String, $order: String) {
    adminSprintsPaged(managerId: $managerId, userId: $userId, status: $status, from: $from, to: $to, limit: $limit, offset: $offset, orderBy: $orderBy, order: $order) {
      total_count
      nodes {
        id
        name
        start_on
        end_on
        status
        progress_percent_cached
        items_total
        items_completed
        user { id allowed_name }
      }
    }
  }
`;

const USER_SEARCH = gql`
  query AdminUserLookup($name: String!, $limit: Int) {
    adminUserLookup(name: $name, limit: $limit) {
      id
      allowed_name
      user_name
    }
  }
`;

const MANAGER_SEARCH = gql`
  query AdminManagerLookup($name: String!, $limit: Int) {
    adminManagerLookup(name: $name, limit: $limit) {
      id
      allowed_name
      user_name
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stats: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
  },
  filters: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  linkButton: {
    textTransform: 'none',
    padding: 0,
  },
}));

const SprintsIndex = () => {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    managerId: '',
    userId: '',
    status: '',
    from: '',
    to: '',
  });
  const [userInput, setUserInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [managerInput, setManagerInput] = useState('');
  const [managerOptions, setManagerOptions] = useState([]);
  // Initialize filters from URL (e.g., /admin/sprints?userId=123&managerId=456)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const userId = params.get('userId') || '';
      const managerId = params.get('managerId') || '';
      const status = params.get('status') || '';
      const from = params.get('from') || '';
      const to = params.get('to') || '';
      if (userId || managerId || status || from || to) {
        setFilters((f) => ({
          ...f,
          userId: userId || f.userId,
          managerId: managerId || f.managerId,
          status: status || f.status,
          from: from || f.from,
          to: to || f.to,
        }));
        setPage(0);
      }
    } catch (_) {}
  }, []);

  const humanize = (value) => {
    const s = String(value || '').toLowerCase();
    return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const badgeBase = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
  };

  const sprintStatusBadgeStyle = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'active':
        return { ...badgeBase, backgroundColor: 'var(--succession-success-bg, rgba(16,185,129,0.1))', color: 'var(--succession-success, #10b981)', border: '1px solid var(--succession-success, #10b981)' };
      case 'abandoned':
        return { ...badgeBase, backgroundColor: 'var(--succession-error-bg, rgba(239,68,68,0.1))', color: 'var(--succession-error, #ef4444)', border: '1px solid var(--succession-error, #ef4444)' };
      case 'closed':
        return { ...badgeBase, backgroundColor: 'var(--succession-bg-secondary, #f8fafc)', color: 'var(--succession-text-secondary, #64748B)', border: '1px solid var(--succession-border, #CBD5E1)' };
      case 'planned':
      default:
        return { ...badgeBase, backgroundColor: 'var(--succession-primary-10, rgba(84,137,182,0.1))', color: 'var(--succession-primary, #5489B6)', border: '1px solid var(--succession-primary, #5489B6)' };
    }
  };

  const variables = useMemo(() => ({
    managerId: filters.managerId || null,
    userId: filters.userId || null,
    status: filters.status || null,
    from: filters.from || null,
    to: filters.to || null,
  }), [filters]);

  const [orderBy, setOrderBy] = useState('start_on');
  const [order, setOrder] = useState('desc');
  const { data, loading, error } = useQuery(GET_ADMIN_SPRINTS_PAGED, {
    variables: { ...variables, limit: rowsPerPage, offset: page * rowsPerPage, orderBy, order },
    fetchPolicy: 'cache-and-network',
  });

  const [runUserSearch] = useLazyQuery(USER_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setUserOptions(res?.adminUserLookup || []);
    },
  });

  const handleUserInputChange = useCallback((e, value) => {
    setUserInput(value);
    if (value && value.length >= 2) {
      runUserSearch({ variables: { name: value, limit: 10 } });
    } else {
      setUserOptions([]);
    }
  }, [runUserSearch]);

  const [runManagerSearch] = useLazyQuery(MANAGER_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setManagerOptions(res?.adminManagerLookup || []);
    },
  });

  const handleManagerInputChange = useCallback((e, value) => {
    setManagerInput(value);
    if (value && value.length >= 2) {
      runManagerSearch({ variables: { name: value, limit: 10 } });
    } else {
      setManagerOptions([]);
    }
  }, [runManagerSearch]);

  const connection = data?.adminSprintsPaged;
  const allRows = connection?.nodes || [];
  const totalCount = connection?.total_count || 0;
  const pagedRows = allRows;

  const stats = useMemo(() => {
    const total = allRows.length;
    const active = allRows.filter(r => r.status === 'ACTIVE').length;
    const avgProgress = total ? (allRows.reduce((a, r) => a + Number(r.progress_percent_cached || 0), 0) / total).toFixed(1) : '0.0';
    const statusCounts = {
      PLANNED: allRows.filter(r => r.status === 'PLANNED').length,
      ACTIVE: active,
      CLOSED: allRows.filter(r => r.status === 'CLOSED').length,
      ABANDONED: allRows.filter(r => r.status === 'ABANDONED').length,
    };
    return { total, active, avgProgress, statusCounts };
  }, [allRows]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const [tab, setTab] = useState('table');

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Sprint Progress</Typography>
        <Box>
          <Button variant="contained" color="primary" component="a" href="/admin/sprints.csv">
            Export CSV
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} className={classes.stats}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography color="textSecondary">Total Sprints</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4" color="primary">{stats.active}</Typography>
              <Typography color="textSecondary">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4">{stats.avgProgress}%</Typography>
              <Typography color="textSecondary">Avg Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Box px={2}>
          <Tabs value={tab} onChange={(_,v)=>setTab(v)} indicatorColor="primary" textColor="primary">
            <Tab value="table" label="Table" />
            <Tab value="charts" label="Charts" />
          </Tabs>
        </Box>

        {tab === 'charts' && (
          <>
            <Box p={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Status Breakdown</Typography>
                <MuiTooltip title="Count of sprints by lifecycle status within the current filters."><IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton></MuiTooltip>
              </Box>
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[{ name: 'Planned', value: stats.statusCounts.PLANNED }, { name: 'Active', value: stats.statusCounts.ACTIVE }, { name: 'Closed', value: stats.statusCounts.CLOSED }, { name: 'Abandoned', value: stats.statusCounts.ABANDONED }] }>
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3f51b5"><LabelList dataKey="value" position="top" /></Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <Box p={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Adoption Over Time</Typography>
                <MuiTooltip title="Number of new active sprints started each month."><IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton></MuiTooltip>
              </Box>
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data?.adminSprintAggregates?.adoption_over_time || []).map(p => ({ name: new Date(p.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), value: p.value }))}>
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#009688"><LabelList dataKey="value" position="top" /></Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <Box p={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Completion Rate Over Time</Typography>
                <MuiTooltip title="Average sprint progress (cached %) for sprints closed each month."><IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton></MuiTooltip>
              </Box>
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={(data?.adminSprintAggregates?.completion_rate_over_time || []).map(p => ({ name: new Date(p.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), value: p.value }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} tickFormatter={(v)=>`${v}%`} />
                    <Tooltip formatter={(v)=>`${Number(v).toFixed(1)}%`} />
                    <Line type="monotone" dataKey="value" stroke="#3f51b5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <Box p={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Blocked % Over Time</Typography>
                <MuiTooltip title="Percent of sprint items that are blocked among items belonging to sprints active that month."><IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton></MuiTooltip>
              </Box>
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={(data?.adminSprintAggregates?.blocked_percent_over_time || []).map(p => ({ name: new Date(p.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), value: p.value }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} tickFormatter={(v)=>`${v}%`} />
                    <Tooltip formatter={(v)=>`${Number(v).toFixed(1)}%`} />
                    <Line type="monotone" dataKey="value" stroke="#e91e63" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            <Box p={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Completion by Category</Typography>
                <MuiTooltip title="For items in sprints within the selected window, completion rate by CEPA category."><IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton></MuiTooltip>
              </Box>
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data?.adminSprintAggregates?.completion_by_category || []).map(p => ({ name: p.category, value: p.percent }))}>
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} tickFormatter={(v)=>`${v}%`} />
                    <Tooltip formatter={(v)=>`${Number(v).toFixed(1)}%`} />
                    <Bar dataKey="value" fill="#4caf50"><LabelList dataKey="value" position="top" formatter={(v)=>`${Number(v).toFixed(0)}%`} /></Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </>
        )}

        {tab === 'table' && (
          <>
        <Box className={classes.filters} p={2}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PLANNED">Planned</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
              <MenuItem value="ABANDONED">Abandoned</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            options={managerOptions}
            getOptionLabel={(opt) => opt?.allowed_name || opt?.user_name || ''}
            onInputChange={handleManagerInputChange}
            onChange={(e, val) => setFilter('managerId', val ? val.id : '')}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" label="Manager" placeholder="Type name…" style={{ minWidth: 220 }} />
            )}
          />
          <Autocomplete
            options={userOptions}
            getOptionLabel={(opt) => opt?.allowed_name || opt?.user_name || ''}
            onInputChange={handleUserInputChange}
            onChange={(e, val) => setFilter('userId', val ? val.id : '')}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" label="User" placeholder="Type name…" style={{ minWidth: 220 }} />
            )}
          />
          <TextField
            variant="outlined"
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={(e) => setFilter('from', e.target.value)}
          />
          <TextField
            variant="outlined"
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </Box>

        <Box className={classes.tableContainer}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}><Typography>Loading…</Typography></Box>
          ) : error ? (
            <Box p={2}><Typography color="error">Error loading sprints</Typography></Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy==='user_name'?order:false}>
                      <TableSortLabel active={orderBy==='user_name'} direction={orderBy==='user_name'?order:'asc'} onClick={() => { setOrderBy('user_name'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='name'?order:false}>
                      <TableSortLabel active={orderBy==='name'} direction={orderBy==='name'?order:'asc'} onClick={() => { setOrderBy('name'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='status'?order:false}>
                      <TableSortLabel active={orderBy==='status'} direction={orderBy==='status'?order:'asc'} onClick={() => { setOrderBy('status'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='start_on'?order:false}>
                      <TableSortLabel active={orderBy==='start_on'} direction={orderBy==='start_on'?order:'asc'} onClick={() => { setOrderBy('start_on'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Start
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='end_on'?order:false}>
                      <TableSortLabel active={orderBy==='end_on'} direction={orderBy==='end_on'?order:'asc'} onClick={() => { setOrderBy('end_on'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        End
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={orderBy==='progress_percent_cached'?order:false}>
                      <TableSortLabel active={orderBy==='progress_percent_cached'} direction={orderBy==='progress_percent_cached'?order:'asc'} onClick={() => { setOrderBy('progress_percent_cached'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Progress %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Completed/Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((s) => (
                    <TableRow key={s.id} hover style={{ cursor: 'pointer' }} onClick={() => history.push(`/admin/sprints/${s.id}`)}>
                      <TableCell>{s.user?.allowed_name}</TableCell>
                      <TableCell>{s.name || '-'}</TableCell>
                      <TableCell>
                        <span style={sprintStatusBadgeStyle(s.status)}>{humanize(s.status)}</span>
                      </TableCell>
                      <TableCell>{s.start_on || '-'}</TableCell>
                      <TableCell>{s.end_on || '-'}</TableCell>
                      <TableCell align="right">{Number(s.progress_percent_cached).toFixed(2)}</TableCell>
                      <TableCell align="right">{s.items_completed}/{s.items_total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SprintsIndex;


